import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Star, 
  Shield, 
  Wifi, 
  MapPin, 
  Users,
  Bed,
  Bath,
  Car,
  Key,
  Sparkles,
  Quote,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import FavoriteButton from '../components/FavoriteButton';
import Recommendations from '../components/Recommendations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Property Card Component - Optimized
const PropertyCard = React.memo(({ property, priority = false }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleCardClick = (e) => {
    // Navigate to property detail unless clicking on the Book Now button
    if (!e.defaultPrevented) {
      window.location.href = `/property/${property.id}`;
    }
  };

  return (
    <div 
      onClick={handleCardClick}
      className="group block bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
      data-testid={`property-card-${property.id}`}
    >
      {/* Image Container - Fixed aspect ratio */}
      <div className="relative aspect-[16/10] bg-slate-800 overflow-hidden">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-slate-700 animate-pulse" />
        )}
        <img
          src={property.images[0]}
          alt={`${property.name} - ${property.bedrooms} bedroom vacation rental in Washington DC`}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading={priority ? 'eager' : 'lazy'}
          onLoad={() => setImageLoaded(true)}
          width="400"
          height="250"
        />
        {/* Favorite Button */}
        <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
          <FavoriteButton propertyId={property.id} />
        </div>
        {/* Price Badge */}
        <div className="absolute top-3 right-3 bg-white px-3 py-1.5 rounded-full shadow-lg">
          <span className="text-base font-bold text-slate-900">${property.pricePerNight}</span>
          <span className="text-xs text-slate-500">/night</span>
        </div>
        {/* Featured Badge */}
        {property.featured && (
          <div className="absolute bottom-3 left-3 bg-amber-500 text-slate-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg">
            Featured
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-amber-600 transition-colors line-clamp-1">
          {property.name}
        </h3>

        <div className="flex items-center text-slate-500 text-sm mb-3">
          <MapPin className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
          <span className="line-clamp-1">Washington DC</span>
        </div>

        {/* Key Highlights */}
        <div className="flex items-center gap-4 text-sm text-slate-600 mb-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-1">
            <Bed className="h-4 w-4 text-slate-400" />
            <span>{property.bedrooms}</span>
          </div>
          <div className="flex items-center gap-1">
            <Bath className="h-4 w-4 text-slate-400" />
            <span>{property.bathrooms}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-slate-400" />
            <span>{property.sleeps}</span>
          </div>
          {property.amenities?.includes('Free Parking on Premises') && (
            <div className="flex items-center gap-1">
              <Car className="h-4 w-4 text-slate-400" />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Link to={`/property/${property.id}`} className="flex-1" onClick={(e) => e.stopPropagation()}>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full text-sm border-slate-200 hover:bg-slate-50"
            >
              View Details
            </Button>
          </Link>
          <Button 
            size="sm" 
            className="flex-1 text-sm bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(property.bookingUrl || '/properties', '_blank');
            }}
          >
            Book Now
          </Button>
        </div>
      </div>
    </div>
  );
});

PropertyCard.displayName = 'PropertyCard';

// Trust Badge Component
const TrustBadge = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
    <Icon className="h-4 w-4 text-amber-400" />
    <span className="text-sm font-medium text-white">{text}</span>
  </div>
);

// Testimonial Component
const Testimonial = ({ quote, author, property }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
    <Quote className="h-6 w-6 text-amber-400 mb-3" />
    <p className="text-slate-600 mb-4 text-sm italic">"{quote}"</p>
    <div className="flex items-center gap-2">
      <div className="flex text-amber-400">
        {[...Array(5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-current" />)}
      </div>
      <span className="text-xs text-slate-500">— {author}</span>
    </div>
  </div>
);

const Home = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/properties`);
        if (!response.ok) throw new Error('Failed to fetch properties');
        const data = await response.json();
        // Convert snake_case to camelCase for frontend compatibility
        const formattedData = data.map(p => ({
          ...p,
          pricePerNight: p.price_per_night,
          bookingUrl: p.booking_url,
          bedConfiguration: p.bed_configuration
        }));
        setProperties(formattedData);
      } catch (err) {
        console.error('Error fetching properties:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  // Sort properties: featured first
  const sortedProperties = useMemo(() => {
    const featured = properties.filter(p => p.featured);
    const nonFeatured = properties.filter(p => !p.featured);
    return [...featured, ...nonFeatured];
  }, [properties]);

  const testimonials = [
    { quote: "Perfect location for exploring DC! Spotless and had everything we needed.", author: "Sarah M.", property: "Capitol Hill 1" },
    { quote: "Amazing rooftop views and so close to the Metro. Highly recommend!", author: "James T.", property: "Capitol View Penthouse" },
    { quote: "Best Airbnb experience. Fast wifi, great workspace, perfect for remote work.", author: "Michelle R.", property: "Capitol Hill 3" }
  ];

  return (
    <div className="bg-slate-900">
      {/* UNIFIED HERO + PROPERTIES SECTION - No gaps */}
      <section className="relative">
        {/* Background Image - DC Spring Cherry Blossoms at Tidal Basin */}
        <div className="absolute inset-0 h-[70vh] md:h-[65vh]">
          <img 
            src="https://images.unsplash.com/photo-1759020622073-49edf74dea96?w=1920&q=80"
            alt="Washington Monument with cherry blossoms at the Tidal Basin in spring"
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-900/30 to-slate-900"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Spacer for fixed header */}
          <div className="pt-24 md:pt-28"></div>
          
          {/* Hero Text & CTAs */}
          <div className="text-center pb-8 md:pb-12">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 leading-tight tracking-tight">
              Premium DC Vacation Rentals
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-6 max-w-xl mx-auto font-light">
              Where comfort, style, and location come together in Washington, DC.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Link to="/properties">
                <Button size="lg" className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-8 py-5 text-base shadow-xl shadow-amber-500/20">
                  Book Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/properties">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/40 text-white hover:bg-white/10 hover:border-white/60 px-8 py-5 text-base backdrop-blur-sm">
                  Browse Properties
                </Button>
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 justify-center">
              <TrustBadge icon={Wifi} text="Fast Wi-Fi" />
              <TrustBadge icon={Key} text="Self Check-in" />
              <TrustBadge icon={MapPin} text="Prime Locations" />
              <TrustBadge icon={Users} text="Family Friendly" />
            </div>
          </div>

          {/* OUR PROPERTIES - Seamlessly connected */}
          <div className="relative z-20 pb-12">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-white">Our Properties</h2>
              <Link to="/properties" className="flex items-center text-amber-400 hover:text-amber-300 font-medium text-sm">
                View All
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            {/* Property Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-400">
                <p>Failed to load properties. Please try again.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {sortedProperties.slice(0, 6).map((property, index) => (
                  <PropertyCard 
                    key={property.id} 
                    property={property} 
                    priority={index < 3}
                  />
                ))}
              </div>
            )}

            {/* View More */}
            {!loading && sortedProperties.length > 6 && (
              <div className="mt-6 text-center">
                <Link to="/properties">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    View All {properties.length} Properties
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WHY STAY WITH US */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Why Stay With Us</h2>
            <p className="text-slate-500">The best experience for your DC stay</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: Star, title: 'Save 15%', desc: 'When you book direct' },
              { icon: Sparkles, title: 'Spotless', desc: 'Pro cleaning' },
              { icon: Wifi, title: 'Fast Wi-Fi', desc: 'Work-ready' },
              { icon: Key, title: 'Self Check-in', desc: '24/7 access' },
              { icon: Shield, title: 'Verified', desc: 'Quality assured' },
              { icon: MapPin, title: 'Prime Location', desc: 'Near Metro' },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-600 mb-3">
                  <item.icon className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-slate-900 text-sm">{item.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUEST REVIEWS */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">What Guests Say</h2>
            <p className="text-slate-500">Real reviews from real travelers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {testimonials.map((testimonial, idx) => (
              <Testimonial key={idx} {...testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* WASHINGTON DC EXPERIENCE */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Explore Washington DC
              </h2>
              <p className="text-slate-600 mb-6">
                Our properties are perfectly positioned near the Capitol, monuments, museums, and top DC neighborhoods.
              </p>
              
              <div className="space-y-3 mb-6">
                {[
                  { label: 'U.S. Capitol', distance: '10 min', tickets: 'https://www.visitthecapitol.gov/plan-visit', directions: 'https://maps.google.com/?q=US+Capitol+Washington+DC' },
                  { label: 'National Mall', distance: '15 min', tickets: null, directions: 'https://maps.google.com/?q=National+Mall+Washington+DC' },
                  { label: 'Smithsonian Museums', distance: '20 min', tickets: 'https://www.si.edu/visit', directions: 'https://maps.google.com/?q=Smithsonian+Institution+Washington+DC' },
                  { label: 'Eastern Market', distance: '3 min', tickets: null, directions: 'https://maps.google.com/?q=Eastern+Market+Washington+DC' },
                  { label: 'Lincoln Memorial', distance: '25 min', tickets: null, directions: 'https://maps.google.com/?q=Lincoln+Memorial+Washington+DC' },
                  { label: 'National Zoo', distance: '15 min', tickets: 'https://nationalzoo.si.edu/visit', directions: 'https://maps.google.com/?q=National+Zoo+Washington+DC' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <span className="font-medium text-slate-900">{item.label}</span>
                      <span className="text-slate-400 text-sm">• {item.distance}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.tickets && (
                        <a 
                          href={item.tickets} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-amber-600 hover:text-amber-700 px-2 py-1 bg-amber-50 rounded hover:bg-amber-100 transition-colors"
                        >
                          Tickets
                        </a>
                      )}
                      <a 
                        href={item.directions} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-slate-600 hover:text-slate-800 px-2 py-1 bg-slate-100 rounded hover:bg-slate-200 transition-colors"
                      >
                        Directions
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/local-guide">
                <Button variant="outline" className="border-slate-300">
                  View Full Local Guide
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1501466044931-62695aada8e9?w=800"
                alt="Washington DC monuments"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-12 md:py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Book Your DC Stay?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            The best rates, flexible cancellation, and personalized service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/properties">
              <Button size="lg" className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-10">
                Book Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 px-10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Personalized Recommendations */}
      <Recommendations />
    </div>
  );
};

export default Home;
